import { RetrofitOption, RetrofitPackage, RetrofitCategory } from '../types/retrofit';
import { Building } from '../types/building';
import { LL97Service } from './ll97Service';

// Retrofit options database with realistic NYC market data
export const RETROFIT_OPTIONS: RetrofitOption[] = [
    {
        id: 'hvac-upgrade',
        name: 'HVAC System Upgrade',
        description: 'High-efficiency heating, ventilation, and air conditioning system',
        category: RetrofitCategory.HVAC,
        averageCostPerSqFt: 15,
        averageEnergyReduction: 25,
        averageGHGReduction: 20,
        typicalPaybackPeriod: 8,
        applicableBuildingTypes: ['Office', 'Retail', 'Mixed Use', 'Hotel']
    },
    {
        id: 'led-lighting',
        name: 'LED Lighting Retrofit',
        description: 'Convert to LED lighting with smart controls',
        category: RetrofitCategory.LIGHTING,
        averageCostPerSqFt: 3,
        averageEnergyReduction: 15,
        averageGHGReduction: 12,
        typicalPaybackPeriod: 4,
        applicableBuildingTypes: ['Office', 'Retail', 'Mixed Use', 'Hotel', 'Multifamily Housing']
    },
    {
        id: 'building-envelope',
        name: 'Building Envelope Improvements',
        description: 'Insulation, windows, and air sealing improvements',
        category: RetrofitCategory.BUILDING_ENVELOPE,
        averageCostPerSqFt: 12,
        averageEnergyReduction: 20,
        averageGHGReduction: 18,
        typicalPaybackPeriod: 10,
        applicableBuildingTypes: ['Office', 'Multifamily Housing', 'Mixed Use']
    },
    {
        id: 'solar-panels',
        name: 'Solar Panel Installation',
        description: 'Rooftop solar photovoltaic system',
        category: RetrofitCategory.RENEWABLE_ENERGY,
        averageCostPerSqFt: 8,
        averageEnergyReduction: 30,
        averageGHGReduction: 35,
        typicalPaybackPeriod: 12,
        applicableBuildingTypes: ['Office', 'Retail', 'Mixed Use', 'Multifamily Housing']
    },
    {
        id: 'energy-management',
        name: 'Building Energy Management System',
        description: 'Smart building controls and energy monitoring',
        category: RetrofitCategory.ENERGY_MANAGEMENT,
        averageCostPerSqFt: 2,
        averageEnergyReduction: 10,
        averageGHGReduction: 8,
        typicalPaybackPeriod: 5,
        applicableBuildingTypes: ['Office', 'Retail', 'Mixed Use', 'Hotel']
    },
    {
        id: 'water-heating',
        name: 'High-Efficiency Water Heating',
        description: 'Heat pump water heaters and efficient systems',
        category: RetrofitCategory.WATER_HEATING,
        averageCostPerSqFt: 4,
        averageEnergyReduction: 12,
        averageGHGReduction: 15,
        typicalPaybackPeriod: 7,
        applicableBuildingTypes: ['Multifamily Housing', 'Hotel', 'Mixed Use']
    }
];

export class RetrofitService {
    
    static generateRetrofitPackages(building: Building): RetrofitPackage[] {
        const applicableOptions = this.getApplicableRetrofitOptions(building);
        const packages: RetrofitPackage[] = [];
        
        // Generate individual packages
        applicableOptions.forEach(option => {
            const singlePackage = this.createRetrofitPackage(`${option.name} Only`, [option], building);
            packages.push(singlePackage);
        });
        
        // Generate combination packages
        if (applicableOptions.length >= 2) {
            // Comprehensive package (all applicable options)
            const comprehensivePackage = this.createRetrofitPackage('Comprehensive Retrofit', applicableOptions, building);
            packages.push(comprehensivePackage);
            
            // Quick wins package (low-cost, high-impact)
            const quickWins = applicableOptions.filter(option => 
                option.averageCostPerSqFt <= 5 && option.typicalPaybackPeriod <= 6
            );
            if (quickWins.length > 0) {
                const quickWinsPackage = this.createRetrofitPackage('Quick Wins Package', quickWins, building);
                packages.push(quickWinsPackage);
            }
        }
        
        return packages.sort((a, b) => b.ll97SavingsVsCost - a.ll97SavingsVsCost);
    }
    
    static getApplicableRetrofitOptions(building: Building): RetrofitOption[] {
        const buildingType = LL97Service.normalizeBuildingType(building.buildingType);
        
        return RETROFIT_OPTIONS.filter(option => 
            option.applicableBuildingTypes.includes(buildingType)
        );
    }
    
    static createRetrofitPackage(name: string, options: RetrofitOption[], building: Building): RetrofitPackage {
        const totalCost = options.reduce((sum, option) => 
            sum + (option.averageCostPerSqFt * building.grossFloorArea), 0
        );
        
        // Calculate combined reductions (not additive, diminishing returns)
        const totalEnergyReduction = this.calculateCombinedReduction(
            options.map(o => o.averageEnergyReduction)
        );
        const totalGHGReduction = this.calculateCombinedReduction(
            options.map(o => o.averageGHGReduction)
        );
        
        // Calculate LL97 savings
        const ll97Calculation = LL97Service.calculateLL97Fine(
            building.buildingType,
            building.grossFloorArea,
            building.totalGHGEmissions
        );
        
        const ll97Savings = LL97Service.calculatePotentialSavings(
            ll97Calculation.currentAnnualFine,
            ll97Calculation.projected2030Fine,
            totalEnergyReduction,
            totalGHGReduction
        );
        
        // Calculate NPV and payback period
        const annualEnergySavings = this.estimateAnnualEnergySavings(building, totalEnergyReduction);
        const totalAnnualSavings = annualEnergySavings + (ll97Calculation.currentAnnualFine * totalGHGReduction / 100);
        const paybackPeriod = totalCost / totalAnnualSavings;
        const npv = this.calculateNPV(totalCost, totalAnnualSavings, 10, 0.07); // 10 years, 7% discount rate
        
        return {
            id: `package-${Date.now()}-${Math.random()}`,
            name,
            retrofitOptions: options,
            totalCost,
            totalEnergyReduction,
            totalGHGReduction,
            combinedPaybackPeriod: paybackPeriod,
            netPresentValue: npv,
            ll97SavingsVsCost: ll97Savings / totalCost
        };
    }
    
    static calculateCombinedReduction(reductions: number[]): number {
        // Account for diminishing returns when combining retrofits
        return reductions.reduce((combined, reduction) => {
            return combined + reduction * (1 - combined / 100);
        }, 0);
    }
    
    static estimateAnnualEnergySavings(building: Building, energyReductionPercent: number): number {
        // Estimate based on NYC average energy costs
        const avgElectricityCost = 0.20; // $/kWh
        const avgGasCost = 1.50; // $/therm
        
        const electricitySavings = building.electricityUse * (energyReductionPercent / 100) * avgElectricityCost;
        const gasSavings = (building.naturalGasUse / 100) * (energyReductionPercent / 100) * avgGasCost; // Convert kBtu to therms
        
        return electricitySavings + gasSavings;
    }
    
    static calculateNPV(initialCost: number, annualSavings: number, years: number, discountRate: number): number {
        let npv = -initialCost;
        
        for (let year = 1; year <= years; year++) {
            npv += annualSavings / Math.pow(1 + discountRate, year);
        }
        
        return npv;
    }
    
    static calculateRetrofitViability(building: Building, retrofitPackage: RetrofitPackage): {
        isViable: boolean;
        confidenceScore: number;
        recommendation: string;
    } {
        const ll97Calculation = LL97Service.calculateLL97Fine(
            building.buildingType,
            building.grossFloorArea,
            building.totalGHGEmissions
        );
        
        const cumulativeLL97Costs = ll97Calculation.cumulativeFines10Year;
        const retrofitROI = retrofitPackage.netPresentValue / retrofitPackage.totalCost;
        const paybackVsLL97 = retrofitPackage.combinedPaybackPeriod <= 10;
        const ll97SavingsRatio = retrofitPackage.ll97SavingsVsCost;
        
        let confidenceScore = 0;
        let recommendation = '';
        
        // Scoring factors
        if (retrofitROI > 0.2) confidenceScore += 30;
        else if (retrofitROI > 0) confidenceScore += 15;
        
        if (paybackVsLL97) confidenceScore += 25;
        
        if (ll97SavingsRatio > 1.5) confidenceScore += 25;
        else if (ll97SavingsRatio > 1.0) confidenceScore += 15;
        
        if (cumulativeLL97Costs > retrofitPackage.totalCost) confidenceScore += 20;
        
        const isViable = confidenceScore >= 70 && ll97SavingsRatio > 1.0;
        
        if (isViable) {
            recommendation = `Highly recommended: This retrofit will save $${Math.round(cumulativeLL97Costs - retrofitPackage.totalCost).toLocaleString()} over 10 years.`;
        } else if (ll97SavingsRatio > 0.8) {
            recommendation = `Consider: Moderate savings potential. May be viable with incentives or financing.`;
        } else {
            recommendation = `Not recommended: LL97 savings do not justify retrofit costs.`;
        }
        
        return { isViable, confidenceScore, recommendation };
    }
}